/* eslint-disable max-len */
/**
@format
@flow
*/
import React from 'react';
import styled from 'styled-components';
import { InputAutocomplete as KrispyInputAutocomplete } from '@krispy/inputs/web';
import Colors from '@ziynx/theme/colors';
import { SubHeading } from '@ziynx/components/typography';
import { Constants } from '@ziynx/components/layout';
import { LoadingSpinner } from '@ziynx/components/utilities';

type Option = {
    text: string
};

type Props = $ReadOnly<{|
    /**
    * Sets the list of options to be shown in the options dropdown.
    */
    options?: Array<Option>,

    /**
     * Show options on focus
     */
    showOptionsOnFocus?: boolean,

    /**
     * Selected options
     */
    selected?: Array<Option>,

    /**
    * If error
    */
    error?: boolean,

    /**
    * If input is disabled
    */
    disabled?: boolean,

    /**
    * If true, input is required
    */
    required?: boolean,

    /**
    * If true, input is read-only, can't edit
    */
    readOnly?: boolean,

    /**
     * Text label for input overall
     */
    label?: string,

    /**
     * Text label for actual input search
     */
    inputLabel?: string,

    /**
    * Aria label for inputs
    * Default is empty string
    * and will be passed to variants with inputName
    * for default
    */
    ariaLabel?: string,

    /**
    * Name for the input to be handled in forms
    */
    inputName?: string,

    /**
    * onClick option property
    */
    onClick?: (event: SyntheticInputEvent<HTMLInputElement>) => void,

    /**
    * onClick property for deletion of selected options
    */
    onSelectedClick?: (event: SyntheticInputEvent<HTMLInputElement>) => void,

    /**
    * Custom onChange fired when user types in input
    */
    onChange?: (event: SyntheticInputEvent<HTMLInputElement>) => void,

    /**
    * Custom onBlur
    */
    onBlur?: (event: SyntheticInputEvent<HTMLInputElement>) => void,

    /**
    * Custom onFocus
    */
    onFocus?: (event: SyntheticInputEvent<HTMLInputElement>) => void,

    /**
    * Custom onSubmit to capture the user hitting Enter / submitting the field
    */
    onSubmit?: (event: SyntheticInputEvent<HTMLInputElement>) => void,

    /**
     * Custom onScroll to capture scrolling through list
     */
    onScroll?: (event: SyntheticEvent<HTMLUListElement>, args?: { hasReachedEnd: boolean }) => void,

    /**
     * Loading state
     */
    isLoading?: boolean,

    /**
    * Custom width
    */
    width?: string,

    /**
     * Render custom selected options
     */
    renderSelected?: any,

    /**
     * Error label to show if there is an error
     */
    errorLabel?: string,

    /**
     * Helper text label
     */
    helperText?: string,
|}>;

const LabelWrapper = styled.div`
  padding-bottom: 24px;
`;

const InputAutocomplete = (props: Props): React$Node => {
  const {
    inputLabel, label, required, onScroll, ...rest
  } = props;

  const handleScroll = (e) => {
    /**
     * Checks if the end of the container has been reached,
     * in which case we should load more if available
     */
    const {
      scrollHeight,
      scrollTop,
      offsetHeight,
    } = e?.target || {};
    const hasReachedEnd = offsetHeight + scrollTop >= scrollHeight;
    // Send as callback with extra info
    onScroll?.(e, { hasReachedEnd });
  };

  return (
    <>
      { !!label && (
        <LabelWrapper>
          <SubHeading
            color={Colors.GrayDark}
            large
          >
            {label}
            {required && (
              <SubHeading
                color={Colors.SecondaryRed}
                primitive="span"
                large
              >
                *
              </SubHeading>
            )}
          </SubHeading>
        </LabelWrapper>
      )}
      <KrispyInputAutocomplete
        {...rest}
        // Don't want to use this listener if no reason to inject data
        onScroll={typeof onScroll === 'function' ? handleScroll : undefined}
        required={required}
        label={inputLabel}
        colorConfig={Colors}
        layoutConfig={Constants}
        loadingSpinner={<LoadingSpinner />}
      />
    </>
  );
};

export default InputAutocomplete;
